import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UpdateProjectInput,
  HolidayWorkEnum,
  ProjectConnection,
  ProjectDetailQuery,
  Project,
  ProjectsFilter,
  ProjectListQuery,
} from '@/types/enterprise';
import { api, fetchRawWithAuth } from '@/lib/api/client';
import { ProjectCreateFormSchema } from '../hooks/useProjectCreateForm';

type ProjectListParams = {
  filter?: ProjectsFilter | null;
  limit: number;
  offset: number;
  enabled?: boolean;
};

export function useProjectListQuery(params: ProjectListParams) {
  const enabled = params.enabled ?? true;
  const filterKey = JSON.stringify(params.filter ?? null);
  const filter = useMemo(() => JSON.parse(filterKey) as ProjectsFilter | null, [filterKey]);
  const path = useMemo(() => buildProjectListPath({ filter, limit: params.limit, offset: params.offset }), [filter, params.limit, params.offset]);
  const [state, setState] = useState<{ data?: ProjectListQuery; fetching: boolean; error?: Error }>({ fetching: enabled });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState((current) => (current.fetching ? { ...current, fetching: false } : current));
      return;
    }
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get(path)
      .then((projects: ProjectConnection) => {
        if (!canceled) setState({ data: { projects }, fetching: false });
      })
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('案件一覧の取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [enabled, reloadKey, path]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

export function useProjectDetailQuery(id: string | undefined) {
  const [state, setState] = useState<{ data?: ProjectDetailQuery; fetching: boolean; error?: Error }>({ fetching: !!id });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get(`/api/v1/enterprise/projects/${id}`)
      .then((project: Project) => {
        if (!canceled) setState({ data: { project }, fetching: false });
      })
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('案件詳細の取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [id, reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

const normalizeDateString = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : value;
};

const toOptionalInt = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return undefined;
  }
  return Math.trunc(num);
};

const mergeEngineerSkillIds = (input: ProjectCreateFormSchema) => {
  const ids = [...(input.engineerSkillIds ?? []), ...(input.engineerSkillInfraIds ?? [])];
  return Array.from(new Set(ids)).filter((id) => id.trim().length > 0);
};

const buildCreateRequest = (input: ProjectCreateFormSchema, options?: { publish?: boolean }) => {
  const month = input.paymentMonth === 'nextMonth' ? '翌月' : input.paymentMonth === 'twoMonths' ? '翌々月' : '';
  const paymenntDate = month + (input.paymentDate != null ? input.paymentDate : '');
  const request = {
    name: input.name,

    jobOverview: input.jobOverview,
    details: input.details,
    expectedRole: input.expectedRole ?? undefined,

    requiredSkill: input.requiredSkill,
    welcomeSkill: input.welcomeSkill,
    desiredPersonnel: input.desiredPersonnel ?? undefined,

    teamComposition: input.teamComposition ?? undefined,
    aboutTeam: input.aboutTeam,

    priceTaxType: input.priceTaxType,
    priceHourlyMax: toOptionalInt(input.priceHourly.max),
    priceHourlyMin: toOptionalInt(input.priceHourly.min),
    priceMonthlyMax: toOptionalInt(input.priceMonthly.max),
    priceMonthlyMin: toOptionalInt(input.priceMonthly.min),

    startDate: normalizeDateString(input.startDate),
    expectedDuration: toOptionalInt(input.expectedDuration),
    canExtendDuration: input.canExtendDuration,
    weeklyWorkingDayMax: toOptionalInt(input.weeklyWorkingDay.max),
    weeklyWorkingDayMin: toOptionalInt(input.weeklyWorkingDay.min),

    paymentDate: paymenntDate,
    priceSettlementHourFrom: toOptionalInt(input.priceSettlementHour.min),
    priceSettlementHourTo: toOptionalInt(input.priceSettlementHour.max),

    developmentSkills: input.developmentSkills,
    officeLocation: input.officeLocation,
    attendanceFrequency: input.attendanceFrequency,
    workingHoursStart: input.workingHoursStart,
    workingHoursEnd: input.workingHoursEnd,
    hasFlexibleSchedule: input.hasFlexibleSchedule,
    coreTimeStart: input.coreTimeStart,
    coreTimeEnd: input.coreTimeEnd,
    holidayWork: input.holidayWork as HolidayWorkEnum,
    engineerPositionIds: input.engineerPositionId ? [input.engineerPositionId] : [],
    engineerSkillIds: mergeEngineerSkillIds(input),
    publish: options?.publish ?? false,
  };

  return request;
};

const buildUpdateRequest = (input: ProjectCreateFormSchema, projectId: string) => {
  const { publish: _publish, ...baseRequest } = buildCreateRequest(input);
  const request = {
    id: projectId,
    ...baseRequest,
  };

  return request;
};

export const useProject = () => {
  const onCreate = async (input: ProjectCreateFormSchema, options?: { publish?: boolean }) => {
    const request = buildCreateRequest(input, options);
    const data = await api.post('/api/v1/enterprise/projects', normalizeProjectRequest(request));
    return { createProject: { data, userErrors: [] } };
  };

  const onUpdate = async (input: ProjectCreateFormSchema, projectId: string) => {
    const request = buildUpdateRequest(input, projectId);
    const { id: _id, ...body } = normalizeProjectRequest(request) as UpdateProjectInput;
    const data = await api.put(`/api/v1/enterprise/projects/${projectId}`, body);
    return { updateProject: { data, userErrors: [] } };
  };

  const onClose = async (projectIds: string[]) => {
    const data = await api.post('/api/v1/enterprise/projects:close', { projectIds });
    return { closeProject: { data: (data as { data?: unknown }).data, userErrors: [] } };
  };

  const onPublish = async (projectId: string) => {
    const data = await api.post(`/api/v1/enterprise/projects/${projectId}/publish`, {});
    return { publishProject: { data, userErrors: [] } };
  };

  const onDuplicate = async (projectId: string) => {
    const data = await api.post(`/api/v1/enterprise/projects/${projectId}/duplicate`, {});
    return { duplicateProject: { data, userErrors: [] } };
  };

  const onUploadHeroImage = async (projectId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetchRawWithAuth(`/api/v1/enterprise/projects/${projectId}/hero-image`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${await response.text()}`);
    }
    const data = await response.json();
    return { uploadProjectHeroImage: { data, userErrors: [] } };
  };

  return { onCreate, onUpdate, onClose, onDuplicate, onPublish, onUploadHeroImage };
};

function buildProjectListPath({ filter, limit, offset }: ProjectListParams) {
  const search = new URLSearchParams();
  search.set('limit', String(limit));
  search.set('offset', String(offset));
  if (filter?.text) search.set('text', String(filter.text));
  if (filter?.textMode) search.set('textMode', String(filter.textMode));
  if (filter?.status) search.set('status', String(filter.status));
  if (filter?.targetAgeMin != null) search.set('targetAgeMin', String(filter.targetAgeMin));
  if (filter?.targetAgeMax != null) search.set('targetAgeMax', String(filter.targetAgeMax));
  if (filter?.engineerPosition?.ids?.length) {
    for (const id of filter.engineerPosition.ids) {
      if (id) search.append('engineerPositionIds', id);
    }
    if (filter.engineerPosition.searchType) search.set('engineerPositionSearchType', String(filter.engineerPosition.searchType));
  }
  if (filter?.engineerSkill?.ids?.length) {
    for (const id of filter.engineerSkill.ids) {
      if (id) search.append('engineerSkillIds', id);
    }
    if (filter.engineerSkill.searchType) search.set('engineerSkillSearchType', String(filter.engineerSkill.searchType));
  }
  return `/api/v1/enterprise/projects?${search.toString()}`;
}

function normalizeProjectRequest<T extends { startDate?: unknown }>(input: T) {
  return {
    ...input,
    startDate: normalizeDateInput(input.startDate),
  };
}

function normalizeDateInput(value: unknown) {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return undefined;
}
